import {Button, closeCurrentDialog, FormButton, FormControl, Separator} from 'platform/components';
import {Box, HStack, Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useId} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {equals, isNotNil} from 'ramda';

import {useTestEmailSmtpCredentialsMutation} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {ApiError, suffixTestId, TestIdProps} from 'shared/web';

import {useSmtpCredentialsTest} from '../hooks/useSmtpCredentialsTest';
import type {EmailServiceFormSchemaType} from '../types/EmailServiceFormSchemaType';
import {getSmtpTestOverallPresentation} from '../utils/getSmtpTestOverallPresentation';
import {toSmtpCredentialsTestValues} from '../utils/toSmtpCredentialsTestValues';
import {SmtpCredentialsTestResult} from './SmtpCredentialsTestResult';

interface EmailServiceFormActionsProps extends TestIdProps {
  control: FormControl<EmailServiceFormSchemaType>;
  formApi: UseFormReturn<EmailServiceFormSchemaType>;
  isCreating: boolean;
  onChangeProvider?: () => void;
}

export function EmailServiceFormActions(props: EmailServiceFormActionsProps) {
  const mutationCacheKey = useId();
  const [testSmtpCredentials, {isLoading, reset}] = useTestEmailSmtpCredentialsMutation({
    fixedCacheKey: mutationCacheKey,
  });
  const {run, testedValues, isInProgress, hasCompletedRun, prepare, cancelPending, start} =
    useSmtpCredentialsTest();
  const [host, port, username, password, emailAddress] = useWatch({
    control: props.control,
    name: ['host', 'port', 'username', 'password', 'emailAddress'],
  });
  const currentValues = toSmtpCredentialsTestValues({
    host,
    port,
    username,
    password,
    emailAddress,
  });
  const isStale = isNotNil(run) && isNotNil(testedValues) && !equals(testedValues, currentValues);
  const overall = isNotNil(run) ? getSmtpTestOverallPresentation(run) : null;
  const isRunning = isLoading || isInProgress;
  const isChecklistVisible = !isStale && (isRunning || isNotNil(run));
  const submitLabel = match({
    isCreating: props.isCreating,
    hasCompletedRun,
    isStale,
    isPassing: overall?.isPassing,
  })
    .with({isCreating: false}, () => i18n.t('general.actions.saveChanges'))
    .with({hasCompletedRun: true, isStale: false, isPassing: false}, () =>
      i18n.t('entity.emailService.actions.createAnyway')
    )
    .otherwise(() => i18n.t('general.actions.create'));

  const handleTestCredentials = async () => {
    const isValid = await props.formApi.trigger();
    if (!isValid) {
      return;
    }

    const valuesToTest = toSmtpCredentialsTestValues(props.formApi.getValues());
    prepare();
    await testSmtpCredentials({testSmtpEmailCredentialsRequestBody: valuesToTest})
      .unwrap()
      .then((response) => start(response.id, valuesToTest))
      .catch((error: ApiError) => {
        cancelPending();
        handleApiError(error);
      })
      .finally(reset);
  };

  return (
    <>
      <Show when={isChecklistVisible}>
        <SmtpCredentialsTestResult
          run={run}
          isRunning={isRunning}
          data-testid={suffixTestId('smtpCredentialsTest', props)}
        />
      </Show>
      <Separator spacing={0} />
      <HStack justify="space-between">
        <Box>
          <Show when={isNotNil(props.onChangeProvider)}>
            <Button
              title={i18n.t('general.actions.back')}
              variant="ghostLink"
              leftIcon="navigation/arrow_back"
              onClick={props.onChangeProvider}
            />
          </Show>
        </Box>
        <HStack spacing={2}>
          <Button
            title={i18n.t('entity.emailService.actions.testCredentials')}
            variant="secondary"
            isDisabled={isRunning}
            onClick={handleTestCredentials}
            data-testid={suffixTestId('testCredentials', props)}
          />
          <Button
            title={i18n.t('general.actions.discard')}
            variant="secondary"
            onClick={closeCurrentDialog}
          />
          <FormButton
            control={props.control}
            type="submit"
            isDisabled={props.isCreating && (!hasCompletedRun || isStale)}
            title={submitLabel}
          />
        </HStack>
      </HStack>
    </>
  );
}
